import {
  ChefHat,
  ExternalLink,
  Package,
} from 'lucide-react';
import type { RecipeUsage } from '../../../types';

interface RecipesTabProps {readonly recipeUsage?: RecipeUsage[];}

const TYPE_LABELS: Record<string, string> = {
  finished: 'Menu Items',
  'sub-recipe': 'Sub-Recipes',
  component: 'Components',
};

export function RecipesTab({ recipeUsage }: RecipesTabProps) {
  if (!recipeUsage?.length) {
    return (
      <EmptyState
        icon={ChefHat}
        title="Not used in any recipes"
        description="This item is not currently used in any recipes."
      />
    );
  }

  const groupedByType: Record<string, RecipeUsage[]> = {};
  for (const usage of recipeUsage) {
    const type = usage.recipeType;
    if (!groupedByType[type]) {
      groupedByType[type] = [];
    }
    groupedByType[type].push(usage);
  }

  const recipeCount = recipeUsage.length;
  const pluralSuffix = recipeCount === 1 ? '' : 's';

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        This item is used in {recipeCount} recipe{pluralSuffix}.
      </p>

      {Object.entries(groupedByType).map(([type, recipes]) => (
        <div key={type}>
          <h3 className="font-medium text-gray-900 mb-3">
            {TYPE_LABELS[type] ?? type} ({recipes.length})
          </h3>
          <div className="space-y-2">
            {recipes.map((recipe) => (
              <div
                key={recipe.recipeId}
                className="flex items-center justify-between rounded-lg border p-3 hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <ChefHat className="h-5 w-5 text-gray-400" />
                  <span className="font-medium text-gray-900">{recipe.recipeName}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>
                    {recipe.quantityPerServing} {recipe.unit} per serving
                  </span>
                  <ExternalLink className="h-4 w-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface EmptyStateProps {
  readonly icon: typeof Package;
  readonly title: string;
  readonly description: string;
}

function EmptyState({
  icon: emptyIcon,
  title,
  description,
}: EmptyStateProps) {
  const IconComponent = emptyIcon;
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-gray-100 p-4 mb-4">
        <IconComponent className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 max-w-sm">{description}</p>
    </div>
  );
}
